import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import {
  Plus, ChevronRight, ChevronDown, Folder, FolderOpen,
  Link2, Trash2, X, Check,
  Calendar, Zap, Archive, GitBranch, Flag, Users,
  BarChart2, BookOpen, Target, Anchor, Navigation,
  ZapOff, FileText, Search, LayoutDashboard,
} from "lucide-react";

// ─── Element type config ──────────────────────────────────────────────────────
const ELEMENT_TYPES = [
  { type: "calendar",     label: "Calendar",       Icon: Calendar,       color: "#7C6CF8", group: "Environments" },
  { type: "workflow",     label: "Workflow",        Icon: Zap,            color: "#F5C842", group: "Environments" },
  { type: "resources",    label: "Resources",       Icon: Archive,        color: "#5EAA8A", group: "Environments" },
  { type: "automations",  label: "Automations",     Icon: Zap,            color: "#FF8A00", group: "Environments" },
  { type: "boards",       label: "Progress",        Icon: Flag,           color: "#A855F7", group: "Tools" },
  { type: "courses",      label: "Courses",         Icon: BookOpen,       color: "#60B8E0", group: "Tools" },
  { type: "commitments",  label: "Commitments",     Icon: Check,          color: "#00C781", group: "Tools" },
  { type: "directory",    label: "Directory",       Icon: Users,          color: "#EC4899", group: "Tools" },
  { type: "capture",      label: "Capture",         Icon: Zap,            color: "#F59E0B", group: "Tools" },
  { type: "ab",           label: "A/B",             Icon: GitBranch,      color: "#14B8A6", group: "Tools" },
  { type: "analytics",    label: "Analytics",       Icon: BarChart2,      color: "#64B5F6", group: "Insights" },
  { type: "compass",      label: "Compass",         Icon: Target,         color: "#5A90F5", group: "Insights" },
  { type: "drift",        label: "Drift",           Icon: Navigation,     color: "#14B8A6", group: "Insights" },
  { type: "tension",      label: "Tension",         Icon: ZapOff,         color: "#EF4444", group: "Insights" },
  { type: "handoff",      label: "Handoff Briefs",  Icon: FileText,       color: "#A855F7", group: "Insights" },
  { type: "folder",       label: "Folder",          Icon: Folder,         color: "#888892", group: "Organize" },
  { type: "shortcut",     label: "Shortcut (URL)",  Icon: Link2,          color: "#888892", group: "Organize" },
];

const TYPE_MAP = Object.fromEntries(ELEMENT_TYPES.map(t => [t.type, t]));
const GROUPS = [...new Set(ELEMENT_TYPES.map(t => t.group))];

const ELEMENT_ROUTES = {
  ab: "/ab", boards: "/boards", courses: "/courses",
  commitments: "/commitments", directory: "/directory",
  capture: "/capture", analytics: "/analytics",
  compass: "/compass", drift: "/drift", tension: "/tension",
  handoff: "/handoff",
};

const LINE_COLOR = "rgba(255,255,255,0.12)";
const CURVE_R = 6; // border-radius on the elbow

// Renders a curved L-shape connector for a tree item
// isLast: whether this is the last child (stops vertical line)
function TreeConnector({ isLast, indent = 0, rowHeight = 30 }) {
  return (
    <div style={{ position: "absolute", left: indent, top: 0, bottom: 0, pointerEvents: "none", width: 16 }}>
      {/* Curved L-shape: vertical line + horizontal bend with radius */}
      <div style={{
        position: "absolute",
        left: 0,
        top: 0,
        height: `calc(50% + 1px)`,
        width: 12,
        borderLeft: `1.5px solid ${LINE_COLOR}`,
        borderBottom: `1.5px solid ${LINE_COLOR}`,
        borderBottomLeftRadius: CURVE_R,
      }} />
      {/* Continuation of vertical line below this item (for non-last children) */}
      {!isLast && (
        <div style={{
          position: "absolute",
          left: 0,
          top: "50%",
          bottom: 0,
          borderLeft: `1.5px solid ${LINE_COLOR}`,
        }} />
      )}
    </div>
  );
}

// ─── Element Palette — centered modal ────────────────────────────────────────
function ElementPalette({ onAdd, onClose, title = "Add element" }) {
  return (
    <div className="fixed inset-0 z-[600] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        style={{ background: "#16172A", border: "1px solid #2E3060" }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <p className="text-[15px] font-semibold" style={{ color: "var(--cresc-text)" }}>{title}</p>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ color: "var(--cresc-text-3)" }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--cresc-surface-3)"}
            onMouseLeave={e => e.currentTarget.style.background = ""}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Groups */}
        <div className="px-5 pb-5 space-y-4 max-h-[70vh] overflow-y-auto" style={{ scrollbarWidth: "none" }}>
          {GROUPS.map(group => (
            <div key={group}>
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--cresc-text-4)" }}>{group}</p>
              <div className="grid grid-cols-3 gap-2">
                {ELEMENT_TYPES.filter(t => t.group === group).map(t => {
                  const Icon = t.Icon;
                  return (
                    <button key={t.type}
                      onClick={() => { onAdd(t.type, t.label); onClose(); }}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all"
                      style={{ border: "1px solid var(--cresc-border)", color: "var(--cresc-text-2)" }}
                      onMouseEnter={e => { e.currentTarget.style.background = "var(--cresc-surface-3)"; e.currentTarget.style.borderColor = t.color + "60"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = "var(--cresc-border)"; }}>
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: t.color + "25" }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: t.color }} />
                      </div>
                      <span className="text-[11px] font-medium">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Context Menu ─────────────────────────────────────────────────────────────
function ContextMenu({ x, y, onDelete, onClose }) {
  return (
    <>
      <div className="fixed inset-0 z-[700]" onClick={onClose} />
      <div className="fixed z-[701] rounded-xl shadow-2xl p-1 w-36"
        style={{ top: y, left: x, background: "#16172A", border: "1px solid #2E3060" }}>
        <button onClick={onDelete}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] text-[#EF4444] transition-colors"
          onMouseEnter={e => e.currentTarget.style.background = "rgba(239,68,68,0.1)"}
          onMouseLeave={e => e.currentTarget.style.background = ""}>
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
    </>
  );
}

// ─── Delete confirmation ──────────────────────────────────────────────────────
function DeleteConfirm({ name, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[800] bg-black/60 flex items-center justify-center p-4">
      <div className="rounded-2xl p-5 w-80 shadow-2xl" style={{ background: "#16172A", border: "1px solid #2E3060" }}>
        <p className="text-[14px] font-semibold mb-1" style={{ color: "var(--cresc-text)" }}>Delete "{name}"?</p>
        <p className="text-[12px] mb-4" style={{ color: "var(--cresc-text-3)" }}>
          This cannot be undone. Nested items will also be deleted.
        </p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 py-2 rounded-xl text-[12px]"
            style={{ border: "1px solid var(--cresc-border)", color: "var(--cresc-text-2)" }}>Cancel</button>
          <button onClick={onConfirm} className="flex-1 py-2 rounded-xl text-[12px] font-semibold text-white"
            style={{ background: "#EF4444" }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── Inline rename input ──────────────────────────────────────────────────────
function InlineRename({ value, onSave, onCancel }) {
  const [val, setVal] = useState(value);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <input ref={ref} value={val}
      onChange={e => setVal(e.target.value)}
      onKeyDown={e => { if (e.key === "Enter") onSave(val); if (e.key === "Escape") onCancel(); }}
      onBlur={() => onSave(val)}
      className="flex-1 bg-transparent outline-none text-[12px] font-medium min-w-0"
      style={{ color: "var(--cresc-text)" }}
      onClick={e => e.stopPropagation()} />
  );
}

// ─── Workflow step row ────────────────────────────────────────────────────────
function StepRow({ step, index, isLast, elementId, wsId, onDelete }) {
  const [hovering, setHovering] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const complete = async (e) => {
    e.stopPropagation();
    try {
      await api.post(`/workspaces/${wsId}/elements/${elementId}/steps/${step.step_id}/complete`);
      toast.success(`"${step.title}" complete — checklist reset ✓`);
    } catch { toast.error("Failed"); }
  };

  return (
    <>
      {/* Tree connector */}
      <div className="relative flex items-center" style={{ paddingLeft: 28 }}>
        <TreeConnector isLast={isLast} indent={0} />

        <div
          className="flex items-center gap-2 py-1 px-2 rounded-lg cursor-pointer flex-1 transition-colors"
          onMouseEnter={e => { setHovering(true); e.currentTarget.style.background = "var(--cresc-surface-2)"; }}
          onMouseLeave={e => { setHovering(false); e.currentTarget.style.background = ""; }}
          onContextMenu={e => { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY }); }}>
          {/* Solid numbered dot */}
          <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-[8px] font-black"
            style={{ background: "var(--cresc-text-2)", color: "var(--cresc-bg)", minWidth: 16 }}>
            {index + 1}
          </div>
          <span className="text-[11px] flex-1 truncate" style={{ color: "var(--cresc-text-2)" }}>{step.title}</span>
          {hovering && (
            <button onClick={complete}
              className="text-[9px] px-1.5 py-0.5 rounded font-semibold flex-shrink-0 transition-colors"
              style={{ background: "var(--cresc-surface-3)", color: "var(--cresc-text-3)" }}>
              ✓
            </button>
          )}
          {step.completed_count > 0 && (
            <span className="text-[9px] px-1 py-0.5 rounded flex-shrink-0"
              style={{ background: "rgba(94,170,138,0.15)", color: "#5EAA8A" }}>
              ×{step.completed_count}
            </span>
          )}
        </div>
      </div>

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)} onDelete={() => { setCtx(null); setConfirm(true); }} />}
      {confirm && (
        <DeleteConfirm name={step.title} onCancel={() => setConfirm(false)}
          onConfirm={async () => {
            await api.delete(`/workspaces/${wsId}/elements/${elementId}/steps/${step.step_id}`);
            setConfirm(false); onDelete(step.step_id);
          }} />
      )}
    </>
  );
}

// ─── Element row ──────────────────────────────────────────────────────────────
function ElementRow({ element, wsId, isLast, onDeleteEl, onUpdate, onSelectElement, selectedId, allElements }) {
  const navigate = useNavigate();
  const [hovering, setHovering] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [steps, setSteps] = useState([]);
  const [stepsLoaded, setStepsLoaded] = useState(false);

  const isActive = selectedId === element.element_id;
  const info = TYPE_MAP[element.type];
  const Icon = info?.Icon || Folder;
  const color = element.color || info?.color || "#888892";
  const isFolder = element.type === "folder";
  const isWorkflow = element.type === "workflow";
  const children = allElements.filter(e => e.parent_id === element.element_id);
  const hasChildren = isFolder ? children.length > 0 : isWorkflow;

  const loadSteps = useCallback(async () => {
    if (!isWorkflow || stepsLoaded) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/elements/${element.element_id}/steps`);
      setSteps(data || []); setStepsLoaded(true);
    } catch {}
  }, [wsId, element.element_id, isWorkflow, stepsLoaded]);

  const handleClick = () => {
    if (isFolder || isWorkflow) {
      setExpanded(v => !v);
      if (isWorkflow && !stepsLoaded) loadSteps();
    }
    onSelectElement(element);
    const route = ELEMENT_ROUTES[element.type];
    if (route) navigate(route);
  };

  const addStep = async () => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/elements/${element.element_id}/steps`, {
        title: `Step ${steps.length + 1}`, element_id: element.element_id,
      });
      setSteps(s => [...s, data]);
      if (!expanded) setExpanded(true);
    } catch { toast.error("Failed to add step"); }
  };

  const saveRename = async (name) => {
    setRenaming(false);
    if (!name.trim() || name === element.name) return;
    try {
      await api.put(`/workspaces/${wsId}/elements/${element.element_id}`, { name: name.trim() });
      onUpdate("rename", { ...element, name: name.trim() });
    } catch {}
  };

  const childrenExpanded = expanded && (isWorkflow ? steps.length > 0 : children.length > 0);

  return (
    <>
      {/* Tree connector from parent */}
      <div className="relative flex items-center" style={{ paddingLeft: 26 }}>
        <TreeConnector isLast={isLast} indent={0} />

        <div
          className="flex items-center gap-2 py-1.5 px-2 rounded-lg cursor-pointer flex-1 transition-colors group/el"
          style={{ background: isActive ? "var(--cresc-surface-3)" : "" }}
          onClick={handleClick}
          onMouseEnter={e => { setHovering(true); if (!isActive) e.currentTarget.style.background = "var(--cresc-surface-2)"; }}
          onMouseLeave={e => { setHovering(false); if (!isActive) e.currentTarget.style.background = ""; }}
          onContextMenu={e => { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY }); }}
          onDoubleClick={e => { e.stopPropagation(); setRenaming(true); }}>

          {/* Expand chevron */}
          {(isFolder || isWorkflow) ? (
            <span className="w-3 h-3 flex items-center justify-center flex-shrink-0" style={{ color: "var(--cresc-text-4)" }}>
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </span>
          ) : <span className="w-3 flex-shrink-0" />}

          {/* Icon */}
          {isFolder ? (
            expanded ? <FolderOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
                     : <Folder className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
          ) : element.type === "shortcut" ? (
            <Link2 className="w-3 h-3 flex-shrink-0" style={{ color }} />
          ) : (
            /* Outlined dot for element */
            <div className="w-3.5 h-3.5 rounded-full border-[2px] flex-shrink-0" style={{ borderColor: color }} />
          )}

          {/* Name */}
          {renaming ? (
            <InlineRename value={element.name} onSave={saveRename} onCancel={() => setRenaming(false)} />
          ) : (
            <span className="text-[12px] font-medium truncate flex-1"
              style={{ color: isActive ? "var(--cresc-text)" : "var(--cresc-text-2)" }}>
              {element.name}
            </span>
          )}

          {/* Hover actions */}
          {hovering && !renaming && (
            <button
              onClick={e => { e.stopPropagation(); isWorkflow ? addStep() : setShowPalette(true); }}
              className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-colors"
              style={{ color: "var(--cresc-text-4)" }}
              onMouseEnter={e => { e.stopPropagation(); e.currentTarget.style.color = "var(--cresc-text-2)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "var(--cresc-text-4)"; }}
              title={isWorkflow ? "Add step" : "Add child element"}>
              <Plus className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Children with vertical tree line */}
      {childrenExpanded && (
        <div className="relative" style={{ paddingLeft: 26 }}>
          {/* Parent vertical continuation line */}
          <div style={{
            position: "absolute", left: 0, top: 0, bottom: 0,
            borderLeft: `1.5px solid ${LINE_COLOR}`,
          }} />
          {isWorkflow
            ? steps.map((step, i) => (
                <StepRow key={step.step_id} step={step} index={i}
                  isLast={i === steps.length - 1}
                  elementId={element.element_id} wsId={wsId}
                  onDelete={id => setSteps(s => s.filter(x => x.step_id !== id))} />
              ))
            : children.map((child, i) => (
                <ElementRow key={child.element_id} element={child} wsId={wsId}
                  isLast={i === children.length - 1}
                  onDeleteEl={onDeleteEl} onUpdate={onUpdate}
                  onSelectElement={onSelectElement} selectedId={selectedId}
                  allElements={allElements} />
              ))
          }
        </div>
      )}

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)} onDelete={() => { setCtx(null); setConfirm(true); }} />}
      {confirm && (
        <DeleteConfirm name={element.name} onCancel={() => setConfirm(false)}
          onConfirm={async () => {
            await api.delete(`/workspaces/${wsId}/elements/${element.element_id}`);
            setConfirm(false); onDeleteEl(element.element_id);
          }} />
      )}
      {showPalette && (
        <ElementPalette
          title={`Add to ${element.name}`}
          onAdd={async (type, defaultName) => {
            try {
              const { data } = await api.post(`/workspaces/${wsId}/elements`, {
                type, name: defaultName, project_id: element.project_id,
                parent_id: element.element_id, color: TYPE_MAP[type]?.color,
              });
              onUpdate("add_child", data);
            } catch { toast.error("Failed"); }
          }}
          onClose={() => setShowPalette(false)} />
      )}
    </>
  );
}

// ─── Project row ──────────────────────────────────────────────────────────────
function ProjectRow({ project, wsId, isLast, onDeleteProject, onUpdate, onSelectElement, onSelectProject, selectedId, selectedProjectId, allElements }) {
  const isProjectActive = selectedProjectId === project.project_id && !selectedId;
  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`cresc:tree:proj:${project.project_id}`) ?? "true"); } catch { return true; }
  });
  const [hovering, setHovering] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [showPalette, setShowPalette] = useState(false);

  const elements = allElements.filter(e => e.project_id === project.project_id && !e.parent_id);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try { localStorage.setItem(`cresc:tree:proj:${project.project_id}`, JSON.stringify(next)); } catch {}
  };

  const saveRename = async (name) => {
    setRenaming(false);
    if (!name.trim() || name === project.name) return;
    try {
      await api.put(`/workspaces/${wsId}/projects/${project.project_id}`, { name: name.trim() });
      onUpdate("rename_project", { ...project, name: name.trim() });
    } catch {}
  };

  const addElement = async (type, defaultName) => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/elements`, {
        type, name: defaultName, project_id: project.project_id,
        color: TYPE_MAP[type]?.color,
      });
      onUpdate("add_element", data);
      if (!expanded) setExpanded(true);
    } catch { toast.error("Failed"); }
  };

  return (
    <>
      {/* Project header row — solid colored dot */}
      <div
        className="flex items-center gap-2 px-2 py-1.5 rounded-xl cursor-pointer transition-colors"
        onClick={() => { toggle(); onSelectProject?.(project); }}
        style={{ background: isProjectActive ? project.color + "20" : "" }}
        onMouseEnter={e => { setHovering(true); if (!isProjectActive) e.currentTarget.style.background = "var(--cresc-surface-2)"; }}
        onMouseLeave={e => { setHovering(false); if (!isProjectActive) e.currentTarget.style.background = isProjectActive ? project.color + "20" : ""; }}
        onContextMenu={e => { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY }); }}
        onDoubleClick={e => { e.stopPropagation(); setRenaming(true); }}>
        <span className="w-3 h-3 flex items-center justify-center flex-shrink-0" style={{ color: "var(--cresc-text-4)" }}>
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </span>
        {/* Solid dot */}
        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: project.color }} />
        {renaming ? (
          <InlineRename value={project.name} onSave={saveRename} onCancel={() => setRenaming(false)} />
        ) : (
          <span className="text-[13px] font-semibold flex-1 truncate" style={{ color: "var(--cresc-text)" }}>
            {project.name}
          </span>
        )}
        {hovering && !renaming && (
          <button
            onClick={e => { e.stopPropagation(); setShowPalette(true); }}
            className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors"
            style={{ color: "var(--cresc-text-4)" }}
            onMouseEnter={e => { e.stopPropagation(); e.currentTarget.style.background = "var(--cresc-surface-3)"; e.currentTarget.style.color = "var(--cresc-text-2)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.color = "var(--cresc-text-4)"; }}
            title="Add element">
            <Plus className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Nested elements with curved tree lines */}
      {expanded && elements.length > 0 && (
        <div className="relative" style={{ paddingLeft: 16 }}>
          {/* Vertical line from project dot down to last element */}
          <div style={{
            position: "absolute", left: 8, top: 0, bottom: 0,
            borderLeft: `1.5px solid ${LINE_COLOR}`,
          }} />
          {elements.map((el, i) => (
            <ElementRow key={el.element_id} element={el} wsId={wsId}
              isLast={i === elements.length - 1}
              onDeleteEl={id => onUpdate("del_element", id)}
              onUpdate={onUpdate}
              onSelectElement={onSelectElement}
              selectedId={selectedId}
              allElements={allElements} />
          ))}
        </div>
      )}

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)} onDelete={() => { setCtx(null); setConfirm(true); }} />}
      {confirm && (
        <DeleteConfirm name={project.name} onCancel={() => setConfirm(false)}
          onConfirm={async () => {
            await api.delete(`/workspaces/${wsId}/projects/${project.project_id}`);
            setConfirm(false); onDeleteProject(project.project_id);
          }} />
      )}
      {showPalette && (
        <ElementPalette
          title={`Add to ${project.name}`}
          onAdd={addElement}
          onClose={() => setShowPalette(false)} />
      )}
    </>
  );
}

// ─── Main ProjectTree ─────────────────────────────────────────────────────────
export default function ProjectTree({ onSelectElement, onSelectProject, onSelectOverview, selectedId, selectedProjectId, isOverviewActive }) {
  const { current } = useWorkspace();
  const wsId = current?.workspace_id;
  const [projects, setProjects] = useState([]);
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addingProject, setAddingProject] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#7C6CF8");
  const newNameRef = useRef(null);

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const [pRes, eRes] = await Promise.all([
        api.get(`/workspaces/${wsId}/projects`),
        api.get(`/workspaces/${wsId}/elements`),
      ]);
      setProjects(pRes.data || []);
      setElements(eRes.data || []);
    } catch {} finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { load(); }, [wsId]);
  useEffect(() => { if (addingProject) setTimeout(() => newNameRef.current?.focus(), 50); }, [addingProject]);

  const createProject = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/projects`, { name: newName.trim(), color: newColor });
      setProjects(p => [...p, data]);
      setNewName(""); setNewColor("#7C6CF8"); setAddingProject(false);
    } catch { toast.error("Failed to create project"); }
  };

  const handleUpdate = (action, payload) => {
    if (action === "rename_project")   setProjects(p => p.map(x => x.project_id === payload.project_id ? payload : x));
    if (action === "add_element")      setElements(e => [...e, payload]);
    if (action === "del_element")      setElements(e => e.filter(x => x.element_id !== payload));
    if (action === "rename")           setElements(e => e.map(x => x.element_id === payload.element_id ? payload : x));
    if (action === "add_child")        setElements(e => [...e, payload]);
  };

  const looseElements = elements.filter(e => !e.project_id && !e.parent_id);
  const COLORS = ["#7C6CF8","#F5C842","#5EAA8A","#EF4444","#60B8E0","#EC4899","#FF8A00","#14B8A6"];
  const filtered = search ? projects.filter(p => p.name.toLowerCase().includes(search.toLowerCase())) : projects;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* ── Overview — top-level dashboard, above all projects ── */}
      <div className="px-2 pb-1 flex-shrink-0">
        <button
          onClick={() => onSelectOverview?.()}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] font-semibold transition-all"
          style={{
            background: isOverviewActive ? "var(--cresc-accent)" : "",
            color: isOverviewActive ? "#000" : "var(--cresc-text-2)",
          }}
          onMouseEnter={e => { if (!isOverviewActive) e.currentTarget.style.background = "var(--cresc-surface-2)"; }}
          onMouseLeave={e => { if (!isOverviewActive) e.currentTarget.style.background = ""; }}>
          <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: isOverviewActive ? "#000" : "var(--cresc-text-3)" }} />
          Overview
        </button>
      </div>
      <div className="px-4 pb-1 flex-shrink-0">
        <div className="h-px" style={{ background: "var(--cresc-border-sub)" }} />
      </div>

      {/* Search */}
      <div className="px-3 pb-2 flex-shrink-0">
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg"
          style={{ background: "var(--cresc-surface-2)", border: "1px solid var(--cresc-border-sub)" }}>
          <Search className="w-3 h-3 flex-shrink-0" style={{ color: "var(--cresc-text-4)" }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search projects…"
            className="flex-1 bg-transparent text-[11px] outline-none"
            style={{ color: "var(--cresc-text)" }} />
          {search && <button onClick={() => setSearch("")} style={{ color: "var(--cresc-text-4)" }}><X className="w-3 h-3" /></button>}
        </div>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto px-2 pb-4" style={{ scrollbarWidth: "none" }}>
        {loading ? (
          <p className="text-[11px] px-3 py-2" style={{ color: "var(--cresc-text-4)" }}>Loading…</p>
        ) : (
          <>
            {/* Loose workspace-root elements */}
            {looseElements.length > 0 && (
              <div className="mb-2">
                {looseElements.map((el, i) => (
                  <ElementRow key={el.element_id} element={el} wsId={wsId}
                    isLast={i === looseElements.length - 1}
                    onDeleteEl={id => setElements(e => e.filter(x => x.element_id !== id))}
                    onUpdate={handleUpdate} onSelectElement={onSelectElement}
                    selectedId={selectedId} allElements={elements} />
                ))}
                <div className="h-px my-2 mx-1" style={{ background: "var(--cresc-border-sub)" }} />
              </div>
            )}

            {/* Projects */}
            {filtered.map((project, i) => (
              <ProjectRow key={project.project_id} project={project} wsId={wsId}
                isLast={i === filtered.length - 1}
                onDeleteProject={id => setProjects(p => p.filter(x => x.project_id !== id))}
                onUpdate={handleUpdate}
                onSelectElement={onSelectElement}
                onSelectProject={onSelectProject}
                selectedId={selectedId}
                selectedProjectId={selectedProjectId}
                allElements={elements} />
            ))}

            {/* New project form */}
            {addingProject ? (
              <div className="mx-1 mt-2 p-3 rounded-xl"
                style={{ border: "1px solid var(--cresc-border)", background: "var(--cresc-surface-2)" }}>
                <input ref={newNameRef} value={newName} onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") createProject(); if (e.key === "Escape") setAddingProject(false); }}
                  placeholder="Project name…"
                  className="w-full bg-transparent text-[12px] outline-none mb-3"
                  style={{ color: "var(--cresc-text)" }} />
                <div className="flex gap-1.5 mb-3 flex-wrap">
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setNewColor(c)}
                      className="w-5 h-5 rounded-full border-2 transition-all"
                      style={{ background: c, borderColor: newColor === c ? "#fff" : "transparent" }} />
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => { setAddingProject(false); setNewName(""); }}
                    className="flex-1 py-1.5 rounded-lg text-[11px]"
                    style={{ border: "1px solid var(--cresc-border)", color: "var(--cresc-text-3)" }}>
                    Cancel
                  </button>
                  <button onClick={createProject} disabled={!newName.trim()}
                    className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold text-black disabled:opacity-40 transition-colors"
                    style={{ background: newColor }}>
                    Create
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingProject(true)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-[12px] transition-colors mt-1"
                style={{ color: "var(--cresc-text-4)" }}
                onMouseEnter={e => { e.currentTarget.style.color = "var(--cresc-text-2)"; e.currentTarget.style.background = "var(--cresc-surface-2)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "var(--cresc-text-4)"; e.currentTarget.style.background = ""; }}>
                <Plus className="w-3.5 h-3.5" /> New project
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
